import * as d3 from 'd3';
import { SwimlaneData, Block, ProcessGroup, EditMode, Line } from './types';
import { STYLES } from './styles/constants';
import { renderMainHeader } from './components/MainHeader';
import { renderSubHeaders } from './components/SubHeaders';
import { renderControls } from './components/Controls';
import { ProcessManager } from './components/ProcessManager';
import { LineManager } from './components/LineManager/LineManager';
import { removeBlocksAndConnections } from './utils/blockUtils';
import { HistoryManager } from './utils/historyManager';
import { addRow, deleteRow, moveRow } from './utils/rowOperations';
import {
  headersStructureKey,
  shouldUseIncrementalSwitch,
} from './utils/incrementalSheetSwitch';
import { markMapSwitchPhase } from './utils/mapSwitchPerf';

export class SwimlaneHeaders {
  private container: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private data: SwimlaneData;
  private blocks: Block[];
  private groups: ProcessGroup[];
  private processManager?: ProcessManager;
  private lineManager?: LineManager;
  private onSave?: (data: SwimlaneData, blocks: Block[], groups: ProcessGroup[]) => void;
  private onEditModeChangeCallback?: (editMode: boolean) => void;
  private onHistoryChangeCallback?: (canUndo: boolean, canRedo: boolean) => void;
  private onBlockSelectCallback?: (block: Block | null) => void;
  private editMode: EditMode = { enabled: false };
  private historyManager: HistoryManager;
  private t: (key: string) => string;
  private mapId: string;
  private setBlocks: React.Dispatch<React.SetStateAction<Block[]>>;
  private blockLayer: string[];
  private lineLayer: string[];
  private attributesDataEditable: any
  private accessToken: string;
  private showWarnings: boolean;
  private showNotesVisible = true;
  private isSampleProcess: boolean;
  private stakeholderPreview?: Record<string, string[]>;
  private processType: string;
  private onStakeholderVerificationLoading?: (loading: boolean) => void;
  private lastLaneHeadersKey = '';
  private addBlocksPersistTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    containerId: string,
    data: SwimlaneData,
    blocks: Block[] = [],
    groups: ProcessGroup[] = [],
    t: (key: string) => string,
    mapId: string,
    setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
    blockLayer: string[],
    lineLayer: string[],
    attributesDataEditable: any,
    accessToken?: string,
    showWarnings: boolean = true,
    isSampleProcess: boolean = false,
    processType?: string,
    stakeholderPreview?: Record<string, string[]>,
    onStakeholderVerificationLoading?: (loading: boolean) => void
  ) {
    this.data = data;
    this.blocks = blocks;
    this.groups = groups;
    this.container = d3.select<HTMLDivElement, unknown>(`#${containerId}`) as unknown as d3.Selection<HTMLDivElement, unknown, null, undefined>;
    this.historyManager = new HistoryManager();
    this.setupContainer();
    this.setupKeyboardShortcuts();
    this.historyManager.push({ data, blocks, groups });
    this.t = t;
    this.mapId = mapId;
    this.setBlocks = setBlocks;
    this.blockLayer = blockLayer;
    this.lineLayer = lineLayer;
    this.attributesDataEditable = attributesDataEditable;
    this.accessToken = accessToken || "";
    this.showWarnings = showWarnings;
    this.isSampleProcess = isSampleProcess;
    this.processType = (processType || '').toString().trim().toUpperCase();
    this.stakeholderPreview = stakeholderPreview;
    this.onStakeholderVerificationLoading = onStakeholderVerificationLoading;
    this.lastLaneHeadersKey = headersStructureKey(data.subHeaderRows?.headers);
    this.render();
  }

  /** À appeler avant updateData lors d’un changement de feuille pour injecter le snapshot backend. */
  public setStakeholderPreview(preview: Record<string, string[]> | undefined): void {
    this.stakeholderPreview = preview;
    if (preview && typeof preview === 'object' && this.processManager) {
      this.processManager.seedStakeholderPreview(preview);
    }
  }

  // In SwimlaneHeaders class
  public toggleLineLayers(): void {
    if (this.lineManager) {
      this.lineManager.toggleLayers();
    }
  }


  private setupContainer(): void {
    this.container
      .style('width', `${this.data.width || STYLES.header.width}px`)
      .style('position', 'relative')
      .style('font-family', 'sans-serif');
  }

  private isTypingTarget(event: KeyboardEvent): boolean {
    const target = (event.target as HTMLElement | null) || (document.activeElement as HTMLElement | null);
    if (!target) return false;
    return (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      Boolean(target.closest('input, textarea, [contenteditable="true"], .attribute-edit-modal'))
    );
  }

  private setupKeyboardShortcuts(): void {
    d3.select('body').on('keydown.swimlane', (event: KeyboardEvent) => {
      if (!this.editMode.enabled) return;

      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z') {
          event.preventDefault();
          this.undo();
        } else if (event.key === 'y') {
          event.preventDefault();
          this.redo();
        }
        return;
      }

      if (this.isTypingTarget(event)) return;

      const isDeleteKey =
        event.key === 'Delete' ||
        event.key === 'Backspace' ||
        event.key === 'Del' ||
        event.code === 'Delete' ||
        event.code === 'Backspace';

      if (!isDeleteKey || event.altKey) return;

      // Always swallow Delete/Backspace in the editor so the browser does not
      // treat Backspace as "history back" (looks like an undo).
      event.preventDefault();
      event.stopPropagation();
      this.deleteSelectedBlocks();
    });
  }

  public render(): void {
    if (this.processManager) {
      this.processManager.markDestroyed();
    }
    this.onStakeholderVerificationLoading?.(false);

    this.container.selectAll('*').remove();

    // Update container width
    this.container.style('width', `${this.data.width || STYLES.header.width}px`);

    renderControls(
      this.container,
      this.data,
      this.blocks,
      this.groups,
      this.editMode,
      this.historyManager,
      this.updateData.bind(this),
      this.toggleEditMode.bind(this)
    );

    renderMainHeader(
      this.container,
      this.data,
      this.handleSave.bind(this)
    );

    renderSubHeaders(
      this.container,
      this.data,
      this.handleSave.bind(this),
      this.editMode,
      this.handleRowOperation.bind(this),
      this.t
    );

    this.processManager = new ProcessManager(
      this.container,
      this.blocks,
      this.groups,
      this.editMode,
      this.handleBlockUpdate.bind(this),
      this.t,
      this.setBlocks,
      this.blockLayer,
      this.lineLayer,
      this.attributesDataEditable,
      this.data.lines || [],
      this.data.subHeaderRows?.headers || [],
      this.showWarnings,
      this.isSampleProcess,
      this.handleLineCreated.bind(this),
      this.handleBlockSelect.bind(this),
      this.mapId,
      this.accessToken,
      this.stakeholderPreview,
      this.processType,
      this.onStakeholderVerificationLoading,
      this.handleBlockDragMove.bind(this)
    );

    this.processManager.setNotesVisible(this.showNotesVisible);

    if (this.data.lines) {
      this.lineManager = new LineManager(
        this.container,
        this.data.lines,
        this.blocks,
        this.handleLineUpdate.bind(this),
        this.editMode,
        this.t,
        this.attributesDataEditable
      );
    }

    // Update history state
    if (this.onHistoryChangeCallback) {
      this.onHistoryChangeCallback(
        this.historyManager.canUndo(),
        this.historyManager.canRedo()
      );
    }
  }

  public switchSheet(
    data: SwimlaneData,
    blocks: Block[],
    groups: ProcessGroup[],
    options?: { resetHistory?: boolean }
  ): void {
    const nextHeadersKey = headersStructureKey(data.subHeaderRows?.headers);
    this.data = data;
    this.blocks = blocks;
    this.groups = groups;

    if (options?.resetHistory) {
      this.historyManager.reset({
        data: this.data,
        blocks: this.blocks,
        groups: this.groups,
      });
      if (this.onHistoryChangeCallback) {
        this.onHistoryChangeCallback(
          this.historyManager.canUndo(),
          this.historyManager.canRedo()
        );
      }
    } else {
      this.saveToHistory();
    }

    markMapSwitchPhase('swimlane_switch_sheet');

    if (this.data.width) {
      this.container.style('width', `${this.data.width}px`);
    }

    if (this.processManager) {
      this.processManager.swapSheet(
        this.blocks,
        this.data.lines || [],
        this.data.subHeaderRows?.headers || [],
        this.mapId,
        { isMapSwitch: true }
      );
    }

    if (this.lineManager && this.data.lines) {
      this.lineManager.updateLines(this.data.lines, this.blocks);
    } else if (this.data.lines && !this.lineManager) {
      this.lineManager = new LineManager(
        this.container,
        this.data.lines,
        this.blocks,
        this.handleLineUpdate.bind(this),
        this.editMode,
        this.t,
        this.attributesDataEditable
      );
    }

    this.lastLaneHeadersKey = nextHeadersKey;
  }

  public getSelectedBlock(): Block | null {
    return this.processManager?.getSelectedBlock() ?? null;
  }

  public deleteBlocks(ids: string[]): boolean {
    if (!this.editMode.enabled || !this.processManager || ids.length === 0) return false;
    const existing = ids.filter((id) => this.blocks.some((block) => block.id === id));
    if (existing.length === 0) return false;
    this.processManager.deleteBlocks(existing);
    return true;
  }

  public deleteSelectedBlocks(): boolean {
    if (!this.processManager) return false;
    return this.deleteBlocks(this.processManager.collectSelectedBlockIds());
  }

  private laneIndexForBlock(block: Block): number {
    const headerHeight = STYLES.header.height + STYLES.header.gap;
    const laneHeight = STYLES.header.laneHeight;
    const relativeY = (block.y || 0) - headerHeight;
    return Math.floor(relativeY / laneHeight);
  }

  /** Add block(s) incrementally — avoids full canvas rebuild on drag-drop / paste. */
  public addBlocks(newBlocks: Block[]): void {
    if (!this.editMode.enabled || !this.processManager || newBlocks.length === 0) return;

    const existingIds = new Set(this.blocks.map((block) => block.id));
    const toAdd = newBlocks.filter((block) => !existingIds.has(block.id));
    if (toAdd.length === 0) return;

    this.blocks = [...this.blocks, ...toAdd];

    toAdd.forEach((block) => {
      const laneIndex = this.laneIndexForBlock(block);
      if (laneIndex >= 0 && laneIndex < this.groups.length) {
        if (!this.groups[laneIndex].groupChildren.includes(block.id)) {
          this.groups[laneIndex].groupChildren.push(block.id);
        }
      } else if (this.groups.length > 0) {
        const fallback = this.groups[0];
        if (!fallback.groupChildren.includes(block.id)) {
          fallback.groupChildren.push(block.id);
        }
      }
    });

    this.processManager.insertBlocks(toAdd);
    this.scheduleAddBlocksPersist();
  }

  /** Create a dotted BPMN association between a symbol and a text annotation. */
  public addAssociation(
    fromId: string,
    toId: string,
    fromSide: 'top' | 'right' | 'bottom' | 'left' = 'right',
    toSide: 'top' | 'right' | 'bottom' | 'left' = 'left'
  ): void {
    if (!this.editMode.enabled) return;
    if (!this.blocks.some((block) => block.id === fromId)) return;
    if (!this.blocks.some((block) => block.id === toId)) return;

    const alreadyLinked = (this.data.lines || []).some((line) => {
      if (line.type !== 'association') return false;
      const source = line.sourceId || line.from;
      const target = line.targetId || line.to;
      return (
        (source === fromId && target === toId) ||
        (source === toId && target === fromId)
      );
    });
    if (alreadyLinked) return;

    const association: Line = {
      id: `association-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      type: 'association',
      sourceId: '',
      targetId: '',
      from: fromId,
      to: toId,
      fromSide,
      toSide,
      text: '',
    };
    this.handleLineCreated(association);
  }

  /** Debounce history + autosave when dropping/pasting several symbols in a row. */
  private scheduleAddBlocksPersist(): void {
    if (this.addBlocksPersistTimer) {
      clearTimeout(this.addBlocksPersistTimer);
    }
    this.addBlocksPersistTimer = setTimeout(() => {
      this.addBlocksPersistTimer = null;
      this.saveToHistory();
      this.handleSave(this.data);
    }, 350);
  }

  public updateData(
    data?: SwimlaneData,
    blocks: Block[] = this.blocks,
    groups: ProcessGroup[] = this.groups,
    options?: { resetHistory?: boolean; isSheetChange?: boolean }
  ): void {
    const nextData = data ?? this.data;
    const nextHeadersKey = headersStructureKey(nextData.subHeaderRows?.headers);
    const useIncremental = shouldUseIncrementalSwitch({
      processManagerExists: Boolean(this.processManager),
      isSheetChange: Boolean(options?.isSheetChange),
      previousHeadersKey: this.lastLaneHeadersKey,
      nextHeadersKey,
      blockCount: blocks.length,
    });

    if (useIncremental) {
      this.switchSheet(nextData, blocks, groups, options);
      return;
    }

    if (data) {
      this.data = data;
    }
    this.blocks = blocks;
    this.groups = groups;
    if (options?.resetHistory) {
      this.historyManager.reset({
        data: this.data,
        blocks: this.blocks,
        groups: this.groups,
      });
      if (this.onHistoryChangeCallback) {
        this.onHistoryChangeCallback(
          this.historyManager.canUndo(),
          this.historyManager.canRedo()
        );
      }
    } else {
      this.saveToHistory();
    }
    markMapSwitchPhase('swimlane_full_render');
    this.render();
    this.lastLaneHeadersKey = nextHeadersKey;
    if (!options?.isSheetChange) {
      this.handleSave(this.data);
    }
  }

  public onSaveCallback(callback: (data: SwimlaneData, blocks: Block[], groups: ProcessGroup[]) => void): this {
    this.onSave = callback;
    return this;
  }

  public onEditModeChange(callback: (editMode: boolean) => void): this {
    this.onEditModeChangeCallback = callback;
    return this;
  }

  public onHistoryChange(callback: (canUndo: boolean, canRedo: boolean) => void): this {
    this.onHistoryChangeCallback = callback;
    if (this.historyManager) {
      callback(this.historyManager.canUndo(), this.historyManager.canRedo());
    }
    return this;
  }

  public onBlockSelect(callback: (block: Block | null) => void): this {
    this.onBlockSelectCallback = callback;
    return this;
  }

  public updateAttributesDataEditable(attributesDataEditable: any): void {
    this.attributesDataEditable = attributesDataEditable;
    this.render();
  }

  public setWarningsEnabled(enabled: boolean): void {
    // Never enable warnings for sample process
    const next = this.isSampleProcess ? false : enabled;
    this.showWarnings = next;
    this.processManager?.setWarningsEnabled(next);
  }

  public forceRenderWarnings(): void {
    this.processManager?.forceRenderWarnings();
  }

  public setNotesVisible(visible: boolean): void {
    this.showNotesVisible = visible;
    this.processManager?.setNotesVisible(visible);
  }

  /** Retire cadres de sélection et menus avant export PNG/PDF. */
  public prepareForExport(): void {
    this.processManager?.prepareForExport();
  }

  public setMapContext(
    mapId: string,
    accessToken?: string,
    laneHeaders?: string[],
    lines?: Line[],
    isSampleProcess?: boolean,
    processType?: string
  ): void {
    this.mapId = mapId;
    this.accessToken = accessToken || this.accessToken;
    if (typeof isSampleProcess === 'boolean') {
      this.isSampleProcess = isSampleProcess;
    }
    if (typeof processType === 'string' && processType.trim()) {
      this.processType = processType.trim().toUpperCase();
    }
    this.processManager?.setMapContext(mapId, accessToken, laneHeaders, lines, this.isSampleProcess, this.processType);
    // If sample, force-hide warnings immediately
    if (this.isSampleProcess) {
      this.setWarningsEnabled(false);
    }
  }

  private handleBlockSelect(block: Block | null): void {
    if (this.onBlockSelectCallback) {
      this.onBlockSelectCallback(block);
    }
  }

  private handleBlockDragMove(blocks: Block[]): void {
    this.blocks = blocks;
    if (this.lineManager && this.data.lines) {
      this.lineManager.updateLines(this.data.lines, blocks);
    }
  }

  private handleSave(data: SwimlaneData): void {
    if (this.onSave) {
      this.onSave(data, this.blocks, this.groups);
    }
  }

  private handleLineUpdate(lines: Line[]): void {
    if (this.data.lines) {
      this.data.lines = lines;
      this.processManager?.updateLines(lines);
      this.saveToHistory();
      this.handleSave(this.data);
    }
  }

  private handleLineCreated(line: Line): void {
    if (!this.data.lines) {
      this.data.lines = [];
    }
    this.data.lines.push(line);
    this.processManager?.updateLines(this.data.lines);
    if (this.lineManager) {
      this.lineManager.updateLines(this.data.lines, this.blocks);
    } else {
      this.lineManager = new LineManager(
        this.container,
        this.data.lines,
        this.blocks,
        this.handleLineUpdate.bind(this),
        this.editMode,
        this.t,
        this.attributesDataEditable
      );
    }
    this.saveToHistory();
    this.handleSave(this.data);
  }

  private handleBlockUpdate(updatedBlocks: Block[], deletedBlockId?: string): void {
    if (deletedBlockId) {
      const deletedIds = new Set(
        this.blocks
          .filter((block) => !updatedBlocks.some((item) => item.id === block.id))
          .map((block) => block.id)
      );
      if (deletedIds.size === 0) {
        deletedIds.add(deletedBlockId);
      }

      const { updatedBlocks: newBlocks, updatedLines } = removeBlocksAndConnections(
        this.blocks,
        deletedIds,
        this.data.lines
      );
      this.blocks = newBlocks;
      if (updatedLines) {
        this.data.lines = updatedLines;
      }
      this.processManager?.updateLines(this.data.lines || []);
      try {
        this.lineManager?.updateLines(this.data.lines || [], this.blocks);
      } catch {
        // Keep the deletion even if line redraw fails.
      }
      this.saveToHistory();
      this.handleSave(this.data);
      this.handleBlockSelect(null);
      return;
    }

    const currentIds = new Set(this.blocks.map((block) => block.id));
    const incomingIds = new Set(updatedBlocks.map((block) => block.id));
    const incomingIsCurrentSet =
      updatedBlocks.length <= this.blocks.length &&
      updatedBlocks.every((block) => currentIds.has(block.id));
    const incomingAddsBlocks = updatedBlocks.length > this.blocks.length &&
      this.blocks.every((block) => incomingIds.has(block.id));

    if (incomingAddsBlocks) {
      this.blocks = updatedBlocks;
    } else if (incomingIsCurrentSet) {
      this.blocks = updatedBlocks;
    } else {
      // Stale updates must not resurrect blocks that were just removed.
      const incomingById = new Map(updatedBlocks.map((block) => [block.id, block]));
      this.blocks = this.blocks.map((block) => incomingById.get(block.id) ?? block);
    }

    if (this.lineManager && this.data.lines) {
      this.lineManager.updateLines(this.data.lines, this.blocks);
    }
    this.saveToHistory();
    this.handleSave(this.data);
  }

  private handleRowOperation(index: number, operation: 'up' | 'down' | 'delete' | 'moveUp' | 'moveDown'): void {
    let result;
    
    if (operation === 'delete') {
      result = deleteRow(this.data, this.blocks, this.groups, index);
    } else if (operation === 'moveUp' || operation === 'moveDown') {
      result = moveRow(this.data, this.blocks, this.groups, index, operation === 'moveUp' ? 'up' : 'down');
    } else {
      result = addRow(this.data, this.blocks, this.groups, index, operation);
    }

    this.data = result.updatedData;
    this.blocks = result.updatedBlocks;
    this.groups = result.updatedGroups;

    this.saveToHistory();
    this.render();
    this.handleSave(this.data);
  }

  public toggleEditMode(): void {
    this.setEditMode(!this.editMode.enabled);
  }

  public setEditMode(enabled: boolean): void {
    if (this.editMode.enabled === enabled) return;
    this.editMode.enabled = enabled;
    if (this.onEditModeChangeCallback) {
      this.onEditModeChangeCallback(this.editMode.enabled);
    }
    if (!this.editMode.enabled) {
      this.handleBlockSelect(null);
    }
    this.render();
  }

  public undo(): void {
    const previousState = this.historyManager.undo();
    if (previousState) {
      this.data = previousState.data;
      this.blocks = previousState.blocks;
      this.groups = previousState.groups;
      this.render();
      this.handleSave(this.data);
    }
  }

  public redo(): void {
    const nextState = this.historyManager.redo();
    if (nextState) {
      this.data = nextState.data;
      this.blocks = nextState.blocks;
      this.groups = nextState.groups;
      this.render();
      this.handleSave(this.data);
    }
  }

  private saveToHistory(): void {
    this.historyManager.push({
      data: this.data,
      blocks: this.blocks,
      groups: this.groups
    });

    if (this.onHistoryChangeCallback) {
      this.onHistoryChangeCallback(
        this.historyManager.canUndo(),
        this.historyManager.canRedo()
      );
    }
  }
}